/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {AbstractRenderGraph} from "App/render/abstract_render_graph";
import Container from "App/utils/container";
import Renderer from "App/renderer";
import Canvas2DContext from "App/canvas_2d_context";
import Text2d from "App/render/canvas/nodes/bins/text_2d";
import WebVRHUD from "App/render/canvas/nodes/bins/web_vr_hud";
import Factory from "App/render/canvas/nodes/bins/factory";
import View from "App/view/view";
import RenderObject from "App/geometry/objects/object";
import IScene from "js_interface/src/scene_interface";
import {EEmbededElementTypes} from "App/model/elements/factory";

/**
 * Change (Reznak): Extends AbstractRenderGraph
 * @param aRenderer
 * @class
 * @augments AbstractRenderGraph
 */
export default
class Canvas2DRenderGraph extends AbstractRenderGraph {

    private _canvas2DmouseMoveListener: any = null;
    private _canvas2DmouseClickListener: any = null;
    private _canvas2DmouseMoveEnabled: boolean = false;

    private _bins: Container = new Container();

    constructor(aSceneAPI: IScene, aRenderer: Renderer, aContext: Canvas2DContext) {
        super(aSceneAPI, aRenderer, aContext);

        this._bins.fill(Factory.createBins(this._sceneAPI, this));

        this.enableMouseMove(true);
    }

    private _decideTargetBin(aShape: RenderObject) {
        switch (aShape.getType()) {
            case "TEXT2D":
                return this._bins.get(Text2d.TypeName);
            case "WEBVRHUD":
                return this._bins.get(WebVRHUD.TypeName);
        }

        return null;
    }

    /**
     * @override
     */
    public resize() {
        // Canvas bins has no resize function
    }

    // IS -------------------------------------------------------------------------

    public isEmpty(): boolean {
        let ret = true;
        this._bins.callForEachItem(function (aBin) {
            if (!aBin.isEmpty()) {
                ret = false;
            }
            return ret;
        });

        return ret;
    }

    public addShape(aShape) {
        let bin = this._decideTargetBin(aShape);
        if (!bin) {
            return false;
        }

        return bin.addShape(aShape);
    }

    /**
     * @override
     * @param aView
     */
    public render(aView: View) {
        if (this.isEmpty()) {
            return;
        }

        // clear canvas 2d first as it is not done automatically as for webgl context
        if (this._context instanceof Canvas2DContext) {
            let position = aView.getPosition();
            let size = aView.getSize();
            this._context.getRenderingContext().clearRect(position[0], position[1], size[0], size[1]);
        }

        // Render all bins
        this._bins.forEach('render', [aView]);
    }

    private mouseClick(aEvent: MouseEvent) {
        this._bins.get(Text2d.TypeName).click();

        // for (let i in this._bins.text2d.getVisibleElements()) {
        //     let ele = this._bins.text2d.getVisibleElements()[i];
        //     if (ele.containsPoint(aEvent.clientX, aEvent.clientY)) {
        //         this._bins.text2d.click(i);
        //         return;
        //     }
        // }
        // this._bins.text2d.highlightNone();
    }

    private mouseMove(aEvent: MouseEvent) {
        // TB note: 2D canvas and webgl canvas has the same dimensions and position so there is no
        // need to do any calculations to get correct x and y
        let text2dBin = this._bins.get(Text2d.TypeName);
        for (let i in text2dBin.getVisibleElements()) {
            let ele = text2dBin.getVisibleElements()[i];
            if (ele.containsPoint(aEvent.clientX, aEvent.clientY)) {
                text2dBin.highlight(i);
                return;
            }
        }

        text2dBin.highlightNone();
    }

    public enableMouseMove(aValue: boolean) {
        if (aValue) {
            if (this._canvas2DmouseMoveListener === null) {
                this._canvas2DmouseMoveListener = this._sceneAPI.addListener('mousemove', this.mouseMove, this);
                this._canvas2DmouseClickListener = this._sceneAPI.addListener('click', this.mouseClick, this);
            }
        } else {
            if (this._canvas2DmouseMoveEnabled) {
                this._sceneAPI.removeListener(this._canvas2DmouseMoveListener);
                this._sceneAPI.removeListener(this._canvas2DmouseClickListener);

                this._canvas2DmouseMoveListener = null;
                this._canvas2DmouseClickListener = null;
            }
        }
        this._canvas2DmouseMoveEnabled = aValue;
    }

    /**
     * Set which GeoEntity types should be rendered.
     * @param aFilter An array of EEmbededElementTypes values.
     */
    public setGeoEntitiesFilter(aFilter: EEmbededElementTypes[]) {
        let bin2D = this._bins.get(Text2d.TypeName);
        if (bin2D) {
            bin2D.setGeoEntitiesFilter(aFilter);
        }
    }

    // getMousePos = function(aCanvas, aEvt) {
    //     let rect = aCanvas.getBoundingClientRect();
    //     return {
    //         x: evt.clientX - rect.left,
    //         y: evt.clientY - rect.top
    //     };
    // };
    //
    // writeMessage = function(canvas, message) {
    //     let context = canvas.getContext('2d');
    //     context.clearRect(0, 0, canvas.width, canvas.height);
    //     context.font = '18pt Calibri';
    //     context.fillStyle = 'black';
    //     context.fillText(message, 10, 25);
    //     logInfo(message);
    // };
    //
}